import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { CustomerVaultsApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

interface UseVaultUpdateParams {
  userId: number;
  vaultId: number;
  tenantId: number;
}

/**
 * Syncs a vault's billing address with the user's current address in PayJunction.
 * The backend pulls address fields (zip, city, state, address) from the user record
 * so no request body is needed.
 */
export const useVaultUpdate = () => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, isSuccess, isError, error } =
    useMutation({
      mutationFn: async ({
        userId,
        vaultId,
        tenantId,
      }: UseVaultUpdateParams) => {
        if (!accessToken) {
          throw new Error("Authentication token is required");
        }

        const service = createAuthenticatedApi(
          CustomerVaultsApi,
          accessToken,
        );

        await service.apiUsersUserIdVaultsVaultIdPut({
          userId,
          vaultId,
          xTenantID: tenantId,
        });

        return { userId, vaultId };
      },
      onSuccess: ({ userId }) => {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.CustomerVaults, userId],
        });
      },
    });

  return { mutate, mutateAsync, isPending, isSuccess, isError, error };
};
